'use client';

import {
    useCallback
    , useEffect
    , useState
} from 'react';
import {
    useDispatch
    , useSelector
} from 'react-redux';

// Ducks
import { StoreState } from '@/ducks/index';
import {
    checkForSession
    , setSelectedBusiness
} from '@/ducks/user';
import {
    selectIsInternalUser
    , selectIsMultiBusinessUser
    , selectIsSessionCheckPending
    , selectSelectedBusiness
    , selectUserLoggedIn
    , selectUserLogoutPending
} from '@/ducks/user/selectors';

// MUI
import LoginLayout from '@/layouts/LoginLayout/LoginLayout';

// Libraries
import {
    usePathname
    , useRouter
} from 'next/navigation';

// Utils
import { selectWfmUserList } from '@/ducks/config/selectors';
import { getCookie } from '@/utils/cookies';
import { getAccessTokenDetails } from '../SchedulerModule/utils/authUtils';

// Hooks
import { useBusinessId } from '@/hooks/useBusinessId';
import { useDevPreferences } from '@/hooks/useDevPreferences';
import { useWFMAccessControls } from '@/hooks/useWFMAccessControls';

const LandingContainer = () => {
    const loggedIn = useSelector( ( state: StoreState ) => selectUserLoggedIn( state ) );
    const logoutPending = useSelector( ( state: StoreState ) => selectUserLogoutPending( state ) );
    const sessionCheckPending = useSelector( ( state: StoreState ) => selectIsSessionCheckPending( state ) );
    const isInternalUser = useSelector( ( state: StoreState ) => selectIsInternalUser( state ) );
    const isMultiBusinessUser = useSelector( ( state: StoreState ) => selectIsMultiBusinessUser( state ) );
    const selectedBusiness = useSelector( ( state: StoreState ) => selectSelectedBusiness( state ) );
    const selectedBusinessId = useBusinessId();
    const wfmUserList = useSelector( ( state: StoreState ) => selectWfmUserList( state ) );
    const [ isMounted, setIsMounted ] = useState<boolean>( false );
    const router = useRouter();
    const pathname = usePathname();
    const [ isDelayPassed, setIsDelayPassed ] = useState<boolean>( false );
    const dispatch = useDispatch();
    const rememberSelectedBusiness = useDevPreferences( 'rememberSelectedBusiness' );
    const disableWFMRedirect = useWFMAccessControls( 'disableWFMRedirect' );

    const redirectLoggedInUser = useCallback( async (): Promise<void> => {
        const fromRoute = sessionStorage?.getItem( 'fromRoute' );
        let path;

        if ( selectedBusiness?.suspensions && selectedBusiness?.suspensions.length ) {
            if ( pathname?.includes( '/suspension' ) ) {
                return;
            }

            router.replace( '/suspension' );
            return;
        }

        if ( isInternalUser ) {
            if ( fromRoute && (
                fromRoute.includes( '/company' )
            ) ) {
                path = '/business-select';
            } else if ( fromRoute ) {
                path = fromRoute;
            } else {
                path = '/business-select';
            }
        } else if ( isMultiBusinessUser ) {
            path = '/business-select';
        } else if ( fromRoute ) {
            path = fromRoute;
        } else {
            path = '/company/dashboard';
        }

        const wfmBusiness = wfmUserList?.includes( selectedBusinessId );

        if ( wfmBusiness && !disableWFMRedirect ) {
            const {
                locationId
                , businessId
            } = await getAccessTokenDetails( selectedBusinessId );

            if ( businessId === selectedBusinessId ) {
                router.push( `/company/scheduler/${ locationId }/summary` );
            }

            return;
        }

        router.push( path );
        return;
    }, [ isInternalUser, isMultiBusinessUser, router, selectedBusinessId, disableWFMRedirect ] );

    useEffect( () => {
        if ( logoutPending ) {
            router.push( '/login' );
        } else if ( loggedIn ) {
            redirectLoggedInUser();
        } else if ( !sessionCheckPending && isMounted && isDelayPassed ) {
            router.push( '/login' );
        }
    }, [ loggedIn, sessionCheckPending, logoutPending, isMounted, isDelayPassed, disableWFMRedirect ] );

    useEffect( () => {
        const fromRoute = sessionStorage?.getItem( 'fromRoute' );

        if ( loggedIn && rememberSelectedBusiness ) {
            const selectedBusiness = JSON.parse( localStorage?.getItem( `devSelectedBusiness_${ process.env.NEXT_PUBLIC_ENVIRONMENT }` ) || 'false' );

            if ( selectedBusiness ) {
                dispatch( setSelectedBusiness( selectedBusiness ) );
                router.push( fromRoute ?? '/company/dashboard' );
            }
        }

    }, [ loggedIn, rememberSelectedBusiness ] );

    const checkForUserSession = useCallback( () => {
        const skipSessionCheckValue = getCookie( 'skipSessionCheck' );

        if ( skipSessionCheckValue ) return;

        dispatch( checkForSession() );
    }, [ dispatch ] );

    useEffect( () => {
        setIsMounted( true );
        setIsDelayPassed( true );
        checkForUserSession();
        return () => {
            setIsMounted( false );
        };
    }, [] );

    return (
        <LoginLayout />
    );
};

export default LandingContainer;
